import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, FormField, Heading, Select, Text } from "grommet";
import { Edit, FormDown, FormUp, Trash } from "grommet-icons";
import { useTranslation } from "react-i18next";
import SchlageService from "../../services/schlage.service";

export default function SettingsSchlage({ hotel, role, onMapped }) {
  // Do not mount the connection UI or make vendor requests for other lock types.
  if (
    hotel?.lockType !== "schlage" ||
    hotel.serviceType === "messaging" ||
    !["admin", "manager"].includes(role)
  )
    return null;
  return <SchlageControls key={hotel._id} hotel={hotel} onMapped={onMapped} />;
}

function SchlageControls({ hotel, onMapped }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [deviceId, setDeviceId] = useState("");
  const [doorId, setDoorId] = useState("");
  const [remapping, setRemapping] = useState(false);
  const active = useRef(false);
  const refreshId = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++refreshId.current;
    const current = () => active.current && request === refreshId.current;
    setLoading(true);
    setError("");
    setInventory(null);
    setDeviceId("");
    setDoorId("");
    try {
      const { data } = await SchlageService.status(hotel._id);
      if (!current()) return;
      setStatus(data);
      if (data.connected && data.subscribed) {
        const devices = await SchlageService.devices(hotel._id);
        if (current()) setInventory(devices.data);
      }
    } catch {
      if (current()) {
        setStatus(null);
        setError("loadError");
      }
    } finally {
      if (current()) setLoading(false);
    }
  }, [hotel._id]);

  useEffect(() => {
    active.current = true;
    refresh();
    return () => {
      active.current = false;
      refreshId.current += 1;
    };
  }, [refresh]);

  const connect = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await SchlageService.authorize(hotel._id);
      if (!active.current) return;
      if (!data?.url) throw new Error("Missing authorization URL");
      window.location.assign(data.url);
    } catch {
      if (active.current) setError("connectError");
    } finally {
      if (active.current) setBusy(false);
    }
  };

  const map = async (unmap = false) => {
    const selected = inventory?.doors.find((door) => door._id === doorId);
    if (unmap && !window.confirm(t("settingsSchlage.confirmUnmap"))) return;
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      const replacement = selected?.lockId
        ? {
            expectedDeviceId: String(selected.lockId),
            ...(unmap ? { unmap: true } : {}),
          }
        : undefined;
      if (replacement)
        await SchlageService.map(hotel._id, doorId, deviceId, replacement);
      else await SchlageService.map(hotel._id, doorId, deviceId);
      if (!active.current) return;
      setSaved(true);
      await refresh();
      if (active.current) onMapped?.();
    } catch {
      if (active.current) setError("mapError");
    } finally {
      if (active.current) setBusy(false);
    }
  };

  const state = !status
    ? "unknown"
    : !status.connected
      ? "disconnected"
      : status.subscribed
        ? "connected"
        : "incomplete";
  const availableDevices =
    inventory?.devices.filter(
      (device) =>
        !inventory.doors.some(
          (door) => door.lockId && String(door.lockId) === String(device.id),
        ),
    ) || [];
  const availableDoors =
    inventory?.doors.filter(
      (door) =>
        !door.lockId ||
        (remapping && (door.lockType || hotel.lockType) === "schlage"),
    ) || [];
  const connected = state === "connected";
  const showDetails = !connected || expanded || Boolean(error);
  const locale = i18n.language?.startsWith("fr") ? "fr" : "en";

  return (
    <Box
      id="schlage"
      border
      round="small"
      pad="medium"
      gap="medium"
      margin={{ bottom: "large" }}
    >
      <Box direction="row" align="center" justify="between" gap="small">
        <Box direction="row" align="center" gap="small" wrap>
          <Heading level="3" margin="none">
            {t("settingsSchlage.title")}
          </Heading>
          {connected && (
            <Box
              background="#e6f4ea"
              round="small"
              pad={{ horizontal: "small", vertical: "xxsmall" }}
            >
              <Text size="small" weight="bold" color="#176b37" role="status">
                {t("settingsSchlage.connected")}
              </Text>
            </Box>
          )}
        </Box>
        {connected && (
          <Button
            icon={showDetails ? <FormUp /> : <FormDown />}
            a11yTitle={t(
              `settingsSchlage.${showDetails ? "collapse" : "expand"}`,
            )}
            aria-expanded={showDetails}
            aria-controls="schlage-details"
            onClick={() => setExpanded(!showDetails)}
            disabled={busy}
          />
        )}
      </Box>
      {showDetails && (
        <Box id="schlage-details" gap="medium">
          <Text>{t("settingsSchlage.property", { name: hotel.name })}</Text>
          <Text>{t("settingsSchlage.description")}</Text>
          {!connected && (
            <Text role="status">
              {t(`settingsSchlage.${loading ? "loading" : state}`)}
            </Text>
          )}
          <Box direction="row" wrap gap="small" style={{ rowGap: 8 }}>
            <Button
              primary
              label={t(
                `settingsSchlage.${status?.connected ? "reconnect" : "connect"}`,
              )}
              onClick={connect}
              disabled={loading || busy}
            />
            <Button
              label={t("settingsSchlage.refresh")}
              onClick={refresh}
              disabled={loading || busy}
            />
            {connected && (
              <Button
                icon={<Edit />}
                a11yTitle={t(
                  `settingsSchlage.${remapping ? "finishRemap" : "remap"}`,
                )}
                label={t(
                  `settingsSchlage.${remapping ? "finishRemap" : "remap"}`,
                )}
                onClick={() => {
                  setRemapping(!remapping);
                  setDoorId("");
                  setDeviceId("");
                  setSaved(false);
                }}
                disabled={loading || busy}
              />
            )}
            {!connected && (
              <Button
                href={`https://www.loxe.io/${locale}/knowledge-base/schlage`}
                target="_blank"
                rel="noopener noreferrer"
                label={t("settingsSchlage.guide")}
              />
            )}
          </Box>
          {error && (
            <Text role="alert" color="status-critical">
              {t(`settingsSchlage.${error}`)}
            </Text>
          )}
          {saved && (
            <Text role="status" color="status-ok">
              {t("settingsSchlage.saved")}
            </Text>
          )}
          {inventory && (
            <Box gap="medium">
              <Text>{t("settingsSchlage.mappingHelp")}</Text>
              {!inventory.devices.length && (
                <Text>{t("settingsSchlage.noDevices")}</Text>
              )}
              {!inventory.doors.length && (
                <Text>{t("settingsSchlage.noDoors")}</Text>
              )}
              {(remapping || !!availableDevices.length) &&
                !!availableDoors.length && (
                  <Box gap="small">
                    <FormField
                      label={t("settingsSchlage.device")}
                      htmlFor="schlage-device"
                    >
                      <Select
                        id="schlage-device"
                        options={availableDevices}
                        value={deviceId}
                        labelKey="name"
                        valueKey={{ key: "id", reduce: true }}
                        placeholder={t("settingsSchlage.selectDevice")}
                        onChange={({ value }) => setDeviceId(value)}
                        disabled={busy}
                      />
                    </FormField>
                    <FormField
                      label={t("settingsSchlage.door")}
                      htmlFor="schlage-door"
                    >
                      <Select
                        id="schlage-door"
                        options={availableDoors.map((door) => ({
                          ...door,
                          label: `${door.name} (${t(`settingsSchlage.${door.type === "room" ? "room" : "entrance"}`)})`,
                        }))}
                        value={doorId}
                        labelKey="label"
                        valueKey={{ key: "_id", reduce: true }}
                        placeholder={t("settingsSchlage.selectDoor")}
                        onChange={({ value }) => setDoorId(value)}
                        disabled={busy}
                      />
                    </FormField>
                    <Button
                      alignSelf="start"
                      primary
                      label={t("settingsSchlage.map")}
                      onClick={() => map()}
                      disabled={busy || !deviceId || !doorId}
                    />
                    {remapping && (
                      <Button
                        alignSelf="start"
                        icon={<Trash />}
                        a11yTitle={t("settingsSchlage.unmap")}
                        label={t("settingsSchlage.unmap")}
                        onClick={() => map(true)}
                        disabled={
                          busy ||
                          !inventory.doors.find((door) => door._id === doorId)
                            ?.lockId
                        }
                      />
                    )}
                  </Box>
                )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
